export type SnapshotStats = {
  total_auditions: number;
  callbacks: number;
  booked: number;
  callback_rate: number | null; // percentage 0-100, null if total = 0
  most_active_month: string | null; // e.g. "March 2025"
  most_common_role_type: string | null;
  most_common_genre: string | null;
};

type AuditionLike = {
  outcome: string | null;
  audition_date: string | null;
  created_at: string;
  role_type: string;
  genres?: string[] | null;
};

export function computeSnapshotStats(auditions: AuditionLike[]): SnapshotStats {
  const total = auditions.length;
  const callbacks = auditions.filter((a) => a.outcome === "Callback").length;
  const booked = auditions.filter((a) => a.outcome === "Booked").length;
  const positive = callbacks + booked;
  const callback_rate = total > 0 ? Math.round((positive / total) * 100) : null;

  // Most active month based on audition_date (fallback to created_at)
  const monthCounts = new Map<string, number>();
  for (const a of auditions) {
    const ds = a.audition_date ?? a.created_at;
    if (!ds) continue;
    const d = new Date(ds);
    if (isNaN(d.getTime())) continue;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    monthCounts.set(key, (monthCounts.get(key) ?? 0) + 1);
  }
  let topMonthKey: string | null = null;
  let topMonthCount = 0;
  for (const [k, v] of monthCounts) {
    if (v > topMonthCount) {
      topMonthCount = v;
      topMonthKey = k;
    }
  }
  let most_active_month: string | null = null;
  if (topMonthKey) {
    const [y, m] = topMonthKey.split("-").map(Number);
    most_active_month = new Date(y, m - 1, 1).toLocaleDateString(undefined, {
      month: "long",
      year: "numeric",
    });
  }

  const topOf = (vals: string[]): string | null => {
    if (!vals.length) return null;
    const counts = new Map<string, number>();
    for (const v of vals) {
      if (!v) continue;
      counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    let top: string | null = null;
    let c = 0;
    for (const [k, v] of counts) {
      if (v > c) {
        c = v;
        top = k;
      }
    }
    return top;
  };

  const most_common_role_type = topOf(auditions.map((a) => a.role_type).filter(Boolean) as string[]);
  const allGenres: string[] = [];
  for (const a of auditions) {
    if (a.genres) allGenres.push(...a.genres);
  }
  const most_common_genre = topOf(allGenres);

  return {
    total_auditions: total,
    callbacks,
    booked,
    callback_rate,
    most_active_month,
    most_common_role_type,
    most_common_genre,
  };
}
